import {
  I18N_CACHE_EXPIRATION_DEV_MS,
  I18N_CACHE_EXPIRATION_MS,
  I18N_CACHE_KEY_PREFIX,
} from "../constants";
import { apiBaseUrl, isLocalOrJenkinsMode } from "../helpers/env.helpers";
import { Log } from "../helpers/log.helpers";
import { normalizeUrlBasePath } from "../helpers/url.helpers";

/** Public endpoint (no auth) returning the deployed API version - same source as the settings drawer. */
const API_VERSION_PATH = "/info/api-dev-version";

/** Where the API version that the cached catalogue was fetched under is remembered between loads. */
const I18N_CACHED_API_VERSION_KEY = "i18nextApiVersion";

/**
 * How long a cached catalogue may be reused.
 *
 * Deployed environments get the long expiry: a release bumps the API version, which invalidates the
 * cache immediately (see `syncI18nCacheWithApiVersion`), so the expiry is only a backstop for a
 * release that ships translations without bumping it.
 *
 * Locally and on the Jenkins/testim environments there is no such bump - a developer editing
 * `en.json` and restarting the API leaves the version untouched - so the cache would hide the edit
 * for hours. Those environments keep the short expiry instead, which is what shipped before this
 * change, so the edit-and-reload loop is unaffected.
 */
export const getI18nCacheExpirationMs = (): number =>
  isLocalOrJenkinsMode()
    ? I18N_CACHE_EXPIRATION_DEV_MS
    : I18N_CACHE_EXPIRATION_MS;

/**
 * Deliberately a bare `fetch` rather than the shared `Api` wrapper: this module is imported by the
 * i18n bootstrap, which runs at module scope, and `ControlsApi` pulls in the helpers barrel — that
 * would close an import cycle back into this same localization module. The i18next HTTP backend
 * fetches the catalogue the same way, and the endpoint is public, so no interceptors are needed.
 */
const fetchApiVersion = async (
  apiBasePath: string,
): Promise<string | undefined> => {
  const url =
    normalizeUrlBasePath(apiBaseUrl() || "") + apiBasePath + API_VERSION_PATH;
  // No credentials: the endpoint is public, and sending them would add a cross-origin CORS
  // requirement that the local landings origin is not allowlisted for.
  const response = await fetch(url);

  if (!response.ok) {
    return undefined;
  }

  // Most endpoints wrap payloads in { status, message, data }, but not all — the translation
  // catalogue itself comes back raw — so accept either shape rather than assuming.
  const payload = (await response.json()) as
    { version?: string; data?: { version?: string } } | undefined;

  return payload?.data?.version ?? payload?.version;
};

/**
 * Drop the cached translation bundles, of every namespace or only `namespace`. i18next-localstorage-backend
 * keys them `i18next_res_<lng>-<ns>`, so the prefix scan covers all languages at once.
 *
 * Only affects what the *next* load reads - the running instance keeps the catalogue it already has
 * in memory. Call it from wherever a reload is already about to happen (see `useVersionCheck`) so
 * that reload picks up a fresh catalogue.
 */
export const clearI18nCache = (namespace?: string): void => {
  Object.keys(window.localStorage)
    .filter(
      key =>
        key.startsWith(I18N_CACHE_KEY_PREFIX) &&
        (!namespace || key.endsWith(`-${namespace}`)),
    )
    .forEach(key => window.localStorage.removeItem(key));
};

/**
 * Invalidate the cached translation catalogue when — and only when — the API has been deployed
 * since the cache was written, so the next load picks up the new one.
 *
 * The catalogue is cached in localStorage for hours, so without this a backend translation change
 * would not surface until the cache expired. The API version cannot be handed to `i18n.init()`
 * instead: init runs at module scope, long before any HTTP response exists, so gating it on the
 * version would load the catalogue twice on every start. This runs *after* init resolves and
 * corrects the cache only on a real version change, which keeps first paint on the cached bundle
 * and costs zero extra translation requests on an ordinary load.
 *
 * It deliberately does NOT call `i18n.reloadResources()` to apply the new catalogue immediately.
 * A reload feeds the fetched bundle to `addResourceBundle` with `deep`/`overwrite` unset, which is
 * a *shallow* top-level merge — so every top-level key is replaced wholesale, discarding the
 * company custom translations that are merged into those same keys on login. That would silently
 * revert a company's custom labels to the product defaults after every deploy, and only sometimes,
 * since it races the login flow that applies them. Clearing the cache is enough: the catalogue is
 * refetched on the next load, before any override is applied.
 *
 * Because it runs in the bootstrap's `.then()`, the catalogue is already in memory by then, so this
 * corrects the *next* load rather than the current session. That is the backend-only case; when a
 * release also ships a new frontend build, `useVersionCheck` clears the cache at the point it
 * queues its reload, so that reload already serves the new catalogue and no session is left behind.
 *
 * Call it from the i18n bootstrap's `.then()` with the same API base path the bootstrap built its
 * catalogue `loadPath` from - each app is served under its own global prefix, so the version has to
 * be read from the API that owns the catalogue. It never throws and never blocks rendering.
 */
export const syncI18nCacheWithApiVersion = async (
  apiBasePath: string,
): Promise<void> => {
  try {
    const apiVersion = await fetchApiVersion(apiBasePath);

    if (!apiVersion) {
      return;
    }

    const cachedApiVersion = window.localStorage.getItem(
      I18N_CACHED_API_VERSION_KEY,
    );

    if (cachedApiVersion === apiVersion) {
      return;
    }

    window.localStorage.setItem(I18N_CACHED_API_VERSION_KEY, apiVersion);

    // First run (or storage cleared): whatever init just loaded is current by definition, so record
    // the version and stop. Clearing here would throw away a fresh catalogue and refetch it.
    if (!cachedApiVersion) {
      return;
    }

    Log.debug(
      `API version changed (${cachedApiVersion} -> ${apiVersion}), dropping cached translations`,
    );
    clearI18nCache();
  } catch (error) {
    // Never let a version check break the app - the expiry backstop still refreshes the cache.
    Log.warn("Could not sync translations with the API version", error);
  }
};
